import { Injectable, Inject } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpResponseBase,
} from '@angular/common/http';
import {map, Observable, tap} from 'rxjs';

import { ServiceBase } from './service-base';
import { DicEntryTranslationDto } from '../models/dic-entry-translation-dto.model';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class DictionaryService extends ServiceBase<DicEntryTranslationDto> {
  constructor(protected override http: HttpClient, @Inject('BASE_URL') baseUrl: string, private authService: AuthService) {
    super(http, baseUrl, 'api/dictionary');
  }

  private translateVersion = '0';

  public getEntriesWithTranslationForUser(
    secondLanId: number = null
  ): Observable<DicEntryTranslationDto[]> {
    const baseHeader = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    let url = `${this.baseUrl}/getentrieswithtranslationforuser/`;
    if (secondLanId !== null) {
      url = `${url}?secondLangId=${secondLanId}`;
    }

    return this.http.get<DicEntryTranslationDto[]>(url, { headers: baseHeader });
  }

  public getEntriesWithTranslation(
    firstLanId: number,
    secondLanId: number
  ): Observable<DicEntryTranslationDto[]> {
    const baseHeader = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http
      .get<DicEntryTranslationDto[]>(
        this.baseUrl +
        `/getentrieswithtranslation/?firstLangId=${firstLanId}&secondLangId=${secondLanId}`,
        {
          headers: baseHeader,
        }
      );
  }

  public getEntryWithTranslation(
    firstLanId: number,
    secondLanId: number,
    entryId: number
  ): Observable<DicEntryTranslationDto> {
    const baseHeader = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http
        .get<DicEntryTranslationDto>(
          this.baseUrl +
            `/getentrywithtranslation/?firstLangId=${firstLanId}&secondLangId=${secondLanId}&entryId=${entryId}`,
          {
            headers: baseHeader,
          }
        );
  }

  public needTranslateReload(lang: string): Observable<boolean> {
    return this.http.get<any>(this.baseUrl + `/version/${lang}`)
      .pipe(
        tap(result => {
          this.translateVersion = result.translateVersion;
        }),
        map(result => {
          return this.translateVersion === '' || result.translateVersion === '' || this.translateVersion !== result.translateVersion
        })
      );
  }

  public getTranslationForEntry(
    firstLanId: number,
    secondLanId: number,
    entryName: string
  ): Observable<DicEntryTranslationDto> {
    const baseHeader = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http
      .get<DicEntryTranslationDto>(
        this.baseUrl +
        `/gettranslationforentry/?firstLangId=${firstLanId}&secondLangId=${secondLanId}&entryName=${entryName}`,
        {
          headers: baseHeader,
        }
      );
  }

  public addEntryTranslation(
    entryId: number,
    langId: number,
    translation: string,
    excluded: boolean = false
  ) {
    const data = {
      EntryId: entryId,
      LangId: langId,
      Translation: translation,
      TranslationExcluded: excluded,
    };

    return this.http
      .post<HttpResponseBase>(`${this.baseUrl}/addentrytranslation`, data, {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      });
  }

  public editEntryTranslation(translationId: number, translation: string) {
    const data = { TranslationId: translationId, Translation: translation };

    return this.http
      .put<HttpResponseBase>(
        `${this.baseUrl}/editentrytranslation/${translationId}`,
        data,
        {
          observe: 'response',
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
          }),
        }
      )
  }

  public deleteEntryTranslation(translationId: number) {
    return this.http
        .delete<HttpResponseBase>(
          `${this.baseUrl}/deleteentrytranslation/${translationId}`,
          {
            headers: new HttpHeaders({
              observe: 'response',
              'Content-Type': 'application/json',
            }),
          }
        )
  }
}
